import type { Locale } from '@fixter/i18n';
import { formatAmountWithCurrency } from '@fixter/i18n';
import Cookies from 'js-cookie';

import { getSegmentFromLocale } from '$util/getSegment';
import type { Campaign } from '$apollo/gql/campaign';
import { getFeatureGlobalPromotionCampaignId, isFeatureGlobalPromotionActive } from '.';

export const getFixterMarketingCampaign = (): string | undefined => {
  return Cookies.get('fixterMarketingCampaign');
};

export const isFixterMarketingCampaignRunning = (): boolean => !!getFixterMarketingCampaign();

export const shouldDisplayMarketingCampaign = (locale: Locale): boolean =>
  isFixterMarketingCampaignRunning() || isFeatureGlobalPromotionActive(locale);

/**
 * Control function that displays the promo banner & discounted prices for products
 * We prioritize the marketing campaign URLs over any other promotion mechanism
 */
export const showMarketingCampaignPrice = (
  locale: Locale,
  sku: string,
  campaign: Campaign | null
): boolean => {
  /**
   * If a campaign is expired or does not exist in our system
   */
  if (!campaign) return false;

  const { affectedItems } = campaign;
  /**
   * all products are included
   */
  if (!affectedItems?.length) return true;

  if (isFixterMarketingCampaignRunning() || isFeatureGlobalPromotionActive(locale))
    return affectedItems.includes(sku);

  return false;
};

export const getFixterMarketingOrGlobalCampaign = (
  locale: Locale,
  campaignServerCookie?: string
): string | null => {
  if (!locale) return null;
  if (campaignServerCookie) return campaignServerCookie;
  const campaignId = getFixterMarketingCampaign();
  if (campaignId) return campaignId;

  if (isFeatureGlobalPromotionActive(locale)) return getFeatureGlobalPromotionCampaignId(locale);
  return null;
};

export const getCampaignDiscount = (
  discountAmountPercentage: Campaign['discountAmountPercentage'],
  discountAmountPence: Campaign['discountAmountPence'],
  locale: Locale
): string => {
  if (discountAmountPercentage) return `${discountAmountPercentage * 100}%`;
  const segment = getSegmentFromLocale(locale);
  return discountAmountPence ? formatAmountWithCurrency(discountAmountPence / 100, segment) : '';
};
